import React, { useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { ROLES } from '../constants/roles';
import BasicInformationTab from './AircraftFormTabs/BasicInformationTab';
import WeightAndFuelTab from './AircraftFormTabs/WeightAndFuelTab';
import WarrantyAndTSNTab from './AircraftFormTabs/WarrantyAndTSNTab';
import OtherDetailsTab from './AircraftFormTabs/OtherDetailsTab';

const AircraftForm = ({
  open,
  onClose,
  selectedAircraft,
  formData,
  setFormData,
  formTab,
  setFormTab,
  dropdownOptions,
  displayTimezone,
  onSubmit,
  onDelete,
  user,
  onCreateOperator,
  onRefreshDropdowns,
  onOpen,
  liveUsage,
  liveUsageLoading,
  liveUsageError
}) => {
  // Refresh dropdowns when form opens (only once, not on every open)
  const hasOpenedRef = React.useRef(false);
  useEffect(() => {
    if (open && onOpen && !hasOpenedRef.current) {
      // Only call onOpen once per form session
      hasOpenedRef.current = true;
      onOpen();
    }
    if (!open) {
      // Reset when form closes
      hasOpenedRef.current = false;
    }
  }, [open, onOpen]);
  
  // Stable onChange handler factory - similar to working forms like OnboardingRequestForm
  const handleChange = useCallback((field, transform = null) => (e) => {
    const value = transform ? transform(e.target.value) : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  }, [setFormData]);

  // Handler for nested objects (currentLocation, warranty, otherDetails)
  const handleNestedChange = useCallback((parentField, childField, transform = null) => (e) => {
    const rawValue = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const value = transform ? transform(rawValue) : rawValue;
    setFormData(prev => ({
      ...prev,
      [parentField]: { ...prev[parentField], [childField]: value }
    }));
  }, [setFormData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth disableRestoreFocus>
      <DialogTitle sx={{ pb: 1.5, fontWeight: 600 }}>
        {selectedAircraft ? 'Edit Aircraft' : 'Add New Aircraft'}
      </DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs 
          value={formTab} 
          onChange={(e, newValue) => setFormTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
              '&.Mui-selected': {
                fontWeight: 600,
                color: 'primary.main'
              }
            }
          }}
        >
          <Tab label="Basic Information" />
          <Tab label="Weight & Fuel" />
          <Tab label="Warranty & TSN" />
          <Tab label="Other Details" />
        </Tabs>
      </Box>
      <DialogContent sx={{ maxHeight: '75vh', overflowY: 'auto', px: 3, py: 2.5 }}>
        {formTab === 0 && (
          <BasicInformationTab
            formData={formData}
            handleChange={handleChange}
            handleNestedChange={handleNestedChange}
            dropdownOptions={dropdownOptions}
            displayTimezone={displayTimezone}
            onCreateOperator={onCreateOperator}
            onRefreshDropdowns={onRefreshDropdowns}
          />
        )}
        {formTab === 1 && (
          <WeightAndFuelTab
            formData={formData}
            handleChange={handleChange}
          />
        )}
        {formTab === 2 && (
          <WarrantyAndTSNTab
            formData={formData}
            setFormData={setFormData}
            handleNestedChange={handleNestedChange}
            displayTimezone={displayTimezone}
            liveUsage={liveUsage}
            liveUsageLoading={liveUsageLoading}
            liveUsageError={liveUsageError}
            user={user}
          />
        )}
        {formTab === 3 && (
          <OtherDetailsTab
            formData={formData}
            handleNestedChange={handleNestedChange}
            displayTimezone={displayTimezone}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
        <Button 
          onClick={onClose}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            px: 2.5
          }}
        >
          Cancel
        </Button>
        {selectedAircraft && user?.role === ROLES.SUPER_ADMIN && (
          <Button
            onClick={() => onDelete(selectedAircraft._id)}
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              px: 2.5
            }}
          >
            Delete
          </Button>
        )}
        <Button 
          onClick={onSubmit} 
          variant="contained"
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          {selectedAircraft ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AircraftForm;
